'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FileText, ClipboardList, DollarSign, Bell, CheckCircle, TrendingUp, TrendingDown, AlertOctagon } from 'lucide-react';



const kpis = [
  { label: 'Titres actifs', value: '1 247', icon: FileText, color: '#1B4F72', bg: '#EBF5FB', trend: '+12', up: true },
  { label: 'Nouvelles demandes en cours', value: '89', icon: ClipboardList, color: '#1E8449', bg: '#EAFAF1', trend: '+5', up: true },
  { label: 'Nouvelles demandes retardées', value: '23', icon: AlertOctagon, color: '#C0392B', bg: '#FDEDEC', trend: '+3', up: false, critical: true },
  { label: 'Recette mensuelle', value: '4,2M $', icon: DollarSign, color: '#D4AC0D', bg: '#FEF9E7', trend: '+8%', up: true },
  { label: 'Dépenses mensuelles', value: '1,1M $', icon: TrendingDown, color: '#C0392B', bg: '#FDEDEC', trend: '+3%', up: false, critical: true },
  { label: 'Alertes critiques', value: '7', icon: Bell, color: '#C0392B', bg: '#FDEDEC', trend: '+1', up: false, critical: true },
];

const revenueData = [
  { day: '01/05', recettes: 180000, budget: 200000 },
  { day: '05/05', recettes: 220000, budget: 200000 },
  { day: '10/05', recettes: 195000, budget: 200000 },
  { day: '15/05', recettes: 310000, budget: 200000 },
  { day: '20/05', recettes: 275000, budget: 200000 },
  { day: '25/05', recettes: 340000, budget: 200000 },
  { day: '30/05', recettes: 290000, budget: 200000 },
  { day: '04/06', recettes: 420000, budget: 200000 },
];

const depensesData = [
  { day: '01/05', depenses: 150000, budget: 180000 },
  { day: '05/05', depenses: 170000, budget: 180000 },
  { day: '10/05', depenses: 210000, budget: 180000 },
  { day: '15/05', depenses: 160000, budget: 180000 },
  { day: '20/05', depenses: 195000, budget: 180000 },
  { day: '25/05', depenses: 230000, budget: 180000 },
  { day: '30/05', depenses: 175000, budget: 180000 },
  { day: '04/06', depenses: 310000, budget: 180000 },
];

const titresStatut = [
  { name: 'Actifs', value: 1247, color: '#1E8449' },
  { name: 'Expirés', value: 234, color: '#C0392B' },
  { name: 'En renouvellement', value: 89, color: '#D4AC0D' },
  { name: 'Suspendus', value: 45, color: '#E67E22' },
];

const topSocietes = [
  { nom: 'Kibali Gold Mining SARL', titres: 24, recettes: '1.2M $', statut: 'Actif' },
  { nom: 'Congo Copper Corporation', titres: 18, recettes: '890K $', statut: 'Actif' },
  { nom: 'Katanga Minerals Ltd', titres: 15, recettes: '760K $', statut: 'Actif' },
  { nom: 'Maniema Resources SA', titres: 12, recettes: '540K $', statut: 'Actif' },
  { nom: 'Ituri Mining Group', titres: 9, recettes: '320K $', statut: 'Partiel' },
];

const decisionsEnAttente = [
  { id: 'DG-2024-089', type: 'Arbitrage', societe: 'Kibali Gold Mining', priorite: 'Urgent', delai: '2j', avis: 'Favorable' },
  { id: 'DG-2024-090', type: 'Signature', societe: 'Congo Copper Corp.', priorite: 'Normal', delai: '5j', avis: 'En attente' },
  { id: 'DG-2024-091', type: 'Autorisation sensible', societe: 'Katanga Minerals', priorite: 'Urgent', delai: '1j', avis: 'Défavorable' },
  { id: 'DG-2024-092', type: 'Renouvellement', societe: 'Maniema Resources', priorite: 'Normal', delai: '7j', avis: 'Favorable' },
];

const demandesData = [
  { mois: 'Jan', recues: 45, approuvees: 38, rejetees: 7 },
  { mois: 'Fév', recues: 52, approuvees: 44, rejetees: 8 },
  { mois: 'Mar', recues: 61, approuvees: 50, rejetees: 11 },
  { mois: 'Avr', recues: 48, approuvees: 42, rejetees: 6 },
  { mois: 'Mai', recues: 70, approuvees: 58, rejetees: 12 },
  { mois: 'Jun', recues: 89, approuvees: 66, rejetees: 23 },
];

export default function DashboardStrategique() {
  const [decisions, setDecisions] = useState(decisionsEnAttente);

  const handleDecision = (id: string, action: 'valider' | 'rejeter') => {
    setDecisions(prev => prev.filter(d => d.id !== id));
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 relative">
      {/* CAMI logo background */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center opacity-[0.04]">
        <Image
          src="/assets/images/image-1781505606681.png"
          alt=""
          width={500}
          height={500}
          className="object-contain"
        />
      </div>
      <div className="relative z-10 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#1B4F72' }}>Poste de Travail DG</h2>
            <p className="text-xs text-gray-400 mt-0.5">Vue d'ensemble stratégique — Directeur Général</p>
          </div>
          <span className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: '#EBF5FB', color: '#1B4F72' }}>
            Juin 2024
          </span>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {kpis.map((kpi, i) => {
            const KpiIcon = kpi.icon;
            return (
              <div
                key={i}
                className="rounded-xl p-4 border cursor-pointer transition-all hover:shadow-md"
                style={{ background: '#fff', borderColor: kpi.critical ? '#C0392B' : '#e5e7eb', borderWidth: kpi.critical ? 2 : 1 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: kpi.bg }}>
                    <KpiIcon size={16} style={{ color: kpi.color }} />
                  </div>
                  <span className={`text-xs font-semibold flex items-center gap-0.5 ${kpi.up ? 'text-green-600' : 'text-red-600'}`}>
                    {kpi.up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {kpi.trend}
                  </span>
                </div>
                <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-tight">{kpi.label}</p>
              </div>
            );
          })}
        </div>

        {/* Charts Row — Recettes + Dépenses */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recettes chart */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-800">Évolution des recettes — 30 derniers jours</h3>
                <p className="text-xs text-gray-400">Recettes encaissées vs budget mensuel</p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: '#EBF5FB', color: '#1B4F72' }}>Mai–Juin 2024</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="recGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1B4F72" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1B4F72" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(v: number) => [`${(v/1000).toFixed(0)}K $`, '']} />
                <Area type="monotone" dataKey="budget" stroke="#D4AC0D" strokeDasharray="4 4" fill="none" name="Budget" strokeWidth={1.5} />
                <Area type="monotone" dataKey="recettes" stroke="#1B4F72" fill="url(#recGrad)" name="Recettes" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Dépenses chart */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-800">Évolution des dépenses — 30 derniers jours</h3>
                <p className="text-xs text-gray-400">Dépenses engagées vs budget mensuel</p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: '#FDEDEC', color: '#C0392B' }}>Mai–Juin 2024</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={depensesData}>
                <defs>
                  <linearGradient id="depGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C0392B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#C0392B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                <Tooltip formatter={(v: number) => [`${(v/1000).toFixed(0)}K $`, '']} />
                <Area type="monotone" dataKey="budget" stroke="#D4AC0D" strokeDasharray="4 4" fill="none" name="Budget" strokeWidth={1.5} />
                <Area type="monotone" dataKey="depenses" stroke="#C0392B" fill="url(#depGrad)" name="Dépenses" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Titres par statut + Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Pie chart */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-semibold text-gray-800 mb-1">Titres par statut</h3>
            <p className="text-xs text-gray-400 mb-3">Répartition actuelle</p>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={titresStatut} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value">
                  {titresStatut.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {titresStatut.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                    <span className="text-gray-600">{s.name}</span>
                  </div>
                  <span className="font-semibold text-gray-800">{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Décisions DG */}
          <div className="lg:col-span-2 bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Décisions DG en attente</h3>
              <span
                className="text-xs font-bold px-2 py-1 rounded-full text-white"
                style={{ background: '#C0392B' }}
              >
                {decisions.length} dossiers
              </span>
            </div>
            <div className="space-y-3">
              {decisions.length === 0 ? (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <CheckCircle size={32} className="mx-auto mb-2 text-green-400" />
                  Aucune décision en attente
                </div>
              ) : (
                decisions.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: '#f0f0f0', background: '#FAFAFA' }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono text-gray-400">{d.id}</span>
                        <span
                          className="text-xs font-semibold px-1.5 py-0.5 rounded"
                          style={{
                            background: d.priorite === 'Urgent' ? '#FDEDEC' : '#EBF5FB',
                            color: d.priorite === 'Urgent' ? '#C0392B' : '#1B4F72',
                          }}
                        >
                          {d.priorite}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-800 truncate">{d.type} — {d.societe}</p>
                      <p className="text-xs text-gray-400">Délai: {d.delai} · Avis: {d.avis}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => handleDecision(d.id, 'valider')}
                        className="px-2 py-1 rounded text-xs font-semibold text-white transition-opacity hover:opacity-80"
                        style={{ background: '#1E8449' }}
                      >
                        Valider
                      </button>
                      <button
                        onClick={() => handleDecision(d.id, 'rejeter')}
                        className="px-2 py-1 rounded text-xs font-semibold text-white transition-opacity hover:opacity-80"
                        style={{ background: '#C0392B' }}
                      >
                        Rejeter
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top Sociétés */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-semibold text-gray-800 mb-3">Top 5 sociétés minières</h3>
            <div className="space-y-2">
              {topSocietes.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span
                    className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                    style={{ background: '#1B4F72' }}
                  >
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{s.nom}</p>
                    <p className="text-xs text-gray-400">{s.titres} titres · {s.recettes}</p>
                  </div>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      background: s.statut === 'Actif' ? '#EAFAF1' : '#FEF5E7',
                      color: s.statut === 'Actif' ? '#1E8449' : '#E67E22',
                    }}
                  >
                    {s.statut}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Nouvelles Demandes chart */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-semibold text-gray-800 mb-3">Évolution des nouvelles demandes</h3>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={demandesData} barSize={8}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mois" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="approuvees" fill="#1E8449" name="Approuvées" radius={[2, 2, 0, 0]} />
                <Bar dataKey="rejetees" fill="#C0392B" name="Rejetées" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
